import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';

const SCROLL_EDGE_THRESHOLD_PX = 32;
const HINT_DURATION_MS = 1800;

@Component({
  standalone: false,
  selector: 'ingsv-adm-horizontal-scroll',
  templateUrl: './horizontal-scroll.component.html',
  styleUrls: ['./horizontal-scroll.component.scss']
})
export class HorizontalScrollComponent implements AfterViewInit, OnDestroy {
  @ViewChild('viewport', { static: true }) viewport!: ElementRef<HTMLElement>;

  canScrollLeft = false;
  canScrollRight = false;
  isOverflowing = false;
  hintActive = false;

  @HostBinding('class.h-scroll-host--overflow')
  get hostOverflowing(): boolean {
    return this.isOverflowing;
  }

  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private hintTimeoutId?: ReturnType<typeof setTimeout>;
  private rafId = 0;
  private hasPlayedHint = false;
  private isUpdating = false;
  private windowUnlisten?: () => void;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    this.setupObservers();
    this.windowUnlisten = this.ngZone.runOutsideAngular(() => {
      const handler = () => this.scheduleRefresh();
      window.addEventListener('resize', handler);
      return () => window.removeEventListener('resize', handler);
    });
    this.scheduleRefresh();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.windowUnlisten?.();
    this.clearHintTimeout();
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
  }

  onViewportScroll(): void {
    this.updateScrollState();
  }

  refresh(): void {
    if (this.isUpdating) {
      return;
    }

    this.isUpdating = true;
    try {
      this.observeProjectedContent();
      this.updateScrollState();
    } finally {
      this.isUpdating = false;
    }
  }

  scrollByDirection(direction: -1 | 1): void {
    const viewport = this.viewport.nativeElement;
    const distance = Math.max(Math.round(viewport.clientWidth * 0.6), 160);
    const options: ScrollToOptions = {
      left: direction * distance,
      behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
    };

    if (typeof viewport.scrollBy === 'function') {
      viewport.scrollBy(options);
      return;
    }

    viewport.scrollLeft += direction * distance;
    this.updateScrollState();
  }

  private setupObservers(): void {
    const viewportEl = this.viewport.nativeElement;

    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleRefresh());
      this.resizeObserver.observe(viewportEl);
    }

    if (typeof MutationObserver === 'function') {
      this.mutationObserver = new MutationObserver(() => this.scheduleRefresh());
      this.mutationObserver.observe(viewportEl, {
        childList: true,
        subtree: true,
      });
    }

    this.observeProjectedContent();
  }

  private observeProjectedContent(): void {
    if (!this.resizeObserver) {
      return;
    }

    const viewportEl = this.viewport.nativeElement;
    const children = viewportEl.children;
    for (let i = 0; i < children.length; i++) {
      this.resizeObserver.observe(children[i]);
    }

    const innerTable = viewportEl.querySelector('table');
    if (innerTable) {
      this.resizeObserver.observe(innerTable);
    }
  }

  private scheduleRefresh(): void {
    if (this.rafId || this.isUpdating) {
      return;
    }

    this.rafId = requestAnimationFrame(() => {
      this.rafId = requestAnimationFrame(() => {
        this.rafId = 0;
        this.ngZone.run(() => this.refresh());
      });
    });
  }

  private updateScrollState(): void {
    const viewport = this.viewport.nativeElement;
    const contentWidth = this.getContentWidth(viewport);
    const maxScrollLeft = Math.max(contentWidth - viewport.clientWidth, 0);
    const nextCanScrollLeft = viewport.scrollLeft > SCROLL_EDGE_THRESHOLD_PX;
    const nextCanScrollRight = viewport.scrollLeft < maxScrollLeft - SCROLL_EDGE_THRESHOLD_PX;
    const nextIsOverflowing = maxScrollLeft > SCROLL_EDGE_THRESHOLD_PX;

    const stateChanged =
      this.canScrollLeft !== nextCanScrollLeft ||
      this.canScrollRight !== nextCanScrollRight ||
      this.isOverflowing !== nextIsOverflowing;

    this.canScrollLeft = nextCanScrollLeft;
    this.canScrollRight = nextCanScrollRight;
    this.isOverflowing = nextIsOverflowing;

    this.maybePlayHint();

    if (stateChanged) {
      this.changeDetectorRef.detectChanges();
    }
  }

  private getContentWidth(viewport: HTMLElement): number {
    const innerTable = viewport.querySelector('table');
    return Math.max(
      viewport.scrollWidth,
      innerTable?.scrollWidth ?? 0,
      innerTable?.offsetWidth ?? 0,
    );
  }

  private maybePlayHint(): void {
    if (this.hasPlayedHint || !this.canScrollRight || this.prefersReducedMotion()) {
      return;
    }

    this.hasPlayedHint = true;
    this.hintActive = true;
    this.changeDetectorRef.detectChanges();
    this.clearHintTimeout();
    this.hintTimeoutId = setTimeout(() => {
      this.hintActive = false;
      this.changeDetectorRef.detectChanges();
    }, HINT_DURATION_MS);
  }

  private clearHintTimeout(): void {
    if (this.hintTimeoutId) {
      clearTimeout(this.hintTimeoutId);
      this.hintTimeoutId = undefined;
    }
  }

  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}
